/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';

(function () {
  /**
   * The possible ways to initiate a restore
   */
  var BulkRestoreType = Object.freeze({
    // The user is restoring object records from a specific backup
    BACKUP: 'BACKUP',
    // The user is manually configuring the restore
    MANUAL: 'MANUAL',
    // The user restoring metadata records from the results of a metadata comparison
    METADATA_COMPARE: 'METADATA_COMPARE',
    // The user is restoring object records from an uploaded CSV that has passed verification
    VERIFIED_CSV: 'VERIFIED_CSV'
  });

  /**
   * The possible ways to manually specify the list of object records to restore.
   */
  var BulkRestoreObjectListType = Object.freeze({
    SEARCH: 'SEARCH',
    UPLOAD: 'UPLOAD',
    BROWSE: 'BROWSE'
  });

  /*
   * This module produces BulkRestoreData objects that maintain the state of a bulk restore.
   */
  angular
    .module('services.bulkrestoredata', ['sbsf.constants', 'services.util', 'sbsf.enums'])
    .constant('BulkRestoreType', BulkRestoreType)
    .constant('BulkRestoreObjectListType', BulkRestoreObjectListType)
    .factory('BulkRestoreDataFactory', [
      '$filter',
      'UtilService',
      'BULK_RESTORE_TYPES',
      'BULK_RESTORE_DEST_TYPES',
      'BULK_RESTORE_CHILD_STRATEGIES',
      'RESTORE_OVERWRITE_STRATEGIES',
      'SOBJECT_STATE',
      'RESTORE_LOOKUP_RELATIONSHIP_ENUM',
      BulkRestoreDataFactory
    ]);

  BulkRestoreDataFactory.$inject = [
    '$filter',
    'UtilService',
    'BULK_RESTORE_TYPES',
    'BULK_RESTORE_DEST_TYPES',
    'BULK_RESTORE_CHILD_STRATEGIES',
    'RESTORE_OVERWRITE_STRATEGIES',
    'SOBJECT_STATE',
    'RESTORE_LOOKUP_RELATIONSHIP_ENUM'
  ];

  /**
   * Defines the factory that supplies a generator used to create new BulkRestoreData objects.
   *
   * @returns {function():BulkRestoreData} a function that will generate a new BulkRestoreData object when called.
   */
  function BulkRestoreDataFactory(
    $filter,
    UtilService,
    BULK_RESTORE_TYPES,
    BULK_RESTORE_DEST_TYPES,
    BULK_RESTORE_CHILD_STRATEGIES,
    RESTORE_OVERWRITE_STRATEGIES,
    SOBJECT_STATE,
    RESTORE_LOOKUP_RELATIONSHIP_ENUM
  ) {
    return {
      // BulkRestoreData is a function that produces a new object
      generate: BulkRestoreData
    };

    /**
     * Returns a new BulkRestoreData object.
     *
     * NOTE: If we had ES6 classes I would probably break this up into a hierarchy of data classes where the data that
     * is specific to a restore type is isolated to the appropriate class. As it is, all the data is mixed into this one
     * object. Classes and typescript would help us get rid of most of the validation that we're doing here.
     *
     * @param {string} [aRestoreType] - The type of restore being performed. Defaults to MANUAL.
     * @param {object} params - The parameters specific to the type of restore.
     * @param {object} params.sourceOrgInfo - The name and sfId of the org from which the data is being restored.
     * @param {number} [params.destType] - a BULK_RESTORE_DEST_TYPES value required for METADATA_COMPARE and
     *   VERIFIED_CSV restores.
     * @param {object} [params.destOrgInfo] - The name and sfId of the org into which the data is being restored.
     *   Required for cross-org METADATA_COMPARE and VERIFIED_CSV restores
     * @param {number} [params.backupBatchId] - The id of the selected backup. Required for BACKUP and METADATA_COMPARE
     *   restores. Identifies the "from" backup for METADATA_COMPARE restores.
     * @param {string} [params.backupBatchDate] - An ISO 8601 string representing the date of the selected backup.
     *   Required for BACKUP and METADATA_COMPARE restores.
     * @param {number} [params.csvBatchId] - The id of the CSV verification batch. Required for
     *   VERIFIED_CSV restores.
     * @param {object} [params.csvCompletedBulkRestoreBatch] - The completed bulk restore batch for a CSV verification.
     * @param {number} [params.csvDuplicateCount] - The number of duplicate records found during CSV verification.
     *   Required for VERIFIED_CSV restores.
     * @param {boolean} [params.csvFailed] - Whether the CSV verification failed for VERIFIED_CSV restores.
     * @param {string} [params.csvFailureMsg] - The failure message for VERIFIED_CSV restores if the verification
     *   failed.
     * @param {string} [params.csvFileName] - The name of the uploaded file for VERIFIED_CSV restores.
     * @param {number} [params.csvInvalidCount] - The number of invalid records found during CSV verification. Required
     *   for VERIFIED_CSV restores.
     * @param {string} [params.csvObjectTypes] - The Salesforce object types for VERIFIED_CSV restores.
     * @param {number} [params.compareBatchId] - The "compare against" id of the selected backup. Required for
     *   METADATA_COMPARE restores.
     * @param {string} [params.compareBatchDate] - An ISO 8601 string representing the date of the "compare against"
     *   backup. Required for METADATA_COMPARE restores.
     * @param {number} [params.recordType] - a BULK_RESTORE_TYPES value for MANUAL OBJECT restores.
     * @param {string} [params.manualObjectListType] - a BulkRestoreObjectListType value for MANUAL OBJECT restores.
     * @param {string} [params.searchObjectType] - the Salesforce object type for MANUAL OBJECT SEARCH and BACKUP
     *   restores.
     * @param {number} [params.searchObjectStates] - an SOBJECT_STATE value for MANUAL OBJECT SEARCH and BACKUP restores.
     * @returns {BulkRestoreData}
     */
    function BulkRestoreData(aRestoreType, params) {
      /**
       * The type of restore being performed. This value cannot be changed after initialization.
       *
       * @type {string} a BulkRestoreTypes value
       */
      var restoreType;

      /**
       * The type of record being restored, either object or metadata. For MANUAL restores the user will specify this.
       * For VERIFIED_CSV and BACKUP restores this is always OBJECT. For METADATA_COMPARE restores this is always
       * METADATA.
       *
       * @type {number} a BULK_RESTORE_TYPES value
       */
      var recordType;

      /**
       * The type of destination, either SAME_ORG or CROSS_ORG. For MANUAL restores the user will specify this. For
       * VERIFIED_CSV and METADATA_COMPARE restores this was specified as part of the verification. For BACKUP restores
       * this is always SAME_ORG.
       *
       * @type {number} a BULK_RESTORE_DEST_TYPES value
       */
      var destType;

      /**
       * The name and sfId for the org for which the current user is logged in. This is the source org for the data to
       * be restored.
       *
       * @type {{id: number, name: string, sfId: string}}
       */
      var sourceOrgInfo;

      /**
       * The name and sfId for the org into which the data is being restored. For a cross-org restore this will
       * be different than the source org. For a same-org restore it will be null/undefined. For a METADATA_COMPARE or
       * VERIFIED_CSV restore this is the originally selected destination org.
       *
       * @type {{id: number, name: string, sfId: string, accessTokenOwnerId: number}}
       */
      var destOrgInfo;

      /**
       * The way the user has chosen to specify the list of object records to be restored. This only applies to MANUAL
       * restores.
       *
       * @type {string} a BulkRestoreObjectListType value
       */
      var manualObjectListType;

      /**
       * Identifies the selected backup for MANUAL OBJECT UPLOAD restores, MANUAL METADATA restores, and BACKUP
       * restores. Identifies the "from" backup for METADATA_COMPARE restores.
       *
       * @type {number}
       */
      var backupBatchId;

      /**
       * An ISO 8601 string representing the date of the backup specified by backupBatchId.
       *
       * @type {string}
       */
      var backupBatchDate;

      /**
       * Identifies the "compare against" backup for METADATA_COMPARE restores. Ignored for other types of restores.
       *
       * @type {number}
       */
      var compareBatchId;

      /**
       * An ISO 8601 string representing the date of the backup specified by compareBatchId.
       *
       * @type {string}
       */
      var compareBatchDate;

      /**
       * Identifies the batch in which the CSV was verified for VERIFIED_CSV restores.
       *
       * @type {number}
       */
      var csvBatchId;

      /**
       * Identifies the "from" backup for MANUAL OBJECT SEARCH restores. Ignored for other types of restores.
       *
       * @type {number}
       */
      var searchStartBatchId;

      /**
       * Identifies the "before" backup for MANUAL OBJECT SEARCH restores. Ignored for other types of restores.
       *
       * @type {number}
       */
      var searchEndBatchId;

      /**
       * The selected Salesforce object type for MANUAL OBJECT SEARCH and BACKUP restores. Ignored for other types of
       * restores.
       *
       * @type {string}
       */
      var searchObjectType;

      /**
       * The "search object name" string for MANUAL OBJECT SEARCH restores. Ignored for other types of restores.
       *
       * @type {string}
       */
      var searchText;

      /**
       * The updated/deleted/new/unchanges selection for MANUAL OBJECT SEARCH and BACKUP restores. Ignored for other types of
       * restores.
       *
       * @type {number[]} an SOBJECT_STATE values array.
       */
      let searchObjectStates = [SOBJECT_STATE.UPDATED];

      /**
       * The selected CSV file for MANUAL OBJECT UPLOAD restores. Ignored for other types of restores.
       *
       * @type {object} a file object.
       */
      var uploadFile;

      /**
       * The Salesforce object types for VERIFIED_CSV restores. Ignored for other types of restores.
       *
       * @type {string[]}
       */
      var csvObjectTypes;

      /**
       * The name of the uploaded CSV file for VERIFIED_CSV restores.
       *
       * @param {string}
       */
      var csvFileName;

      /**
       * The number of duplicate records found during verification for VERIFIED_CSV restores.
       *
       * @type {number}
       */
      var csvDuplicateCount;

      /**
       * The number of invalid records found during verification for VERIFIED_CSV restores.
       *
       * @type {number}
       */
      var csvInvalidCount;

      /**
       * Indicates whether the CSV verification failed for VERIFIED_CSV restores.
       *
       * @type {boolean}
       */
      var csvFailed;

      /**
       * The CSV verification failure message if the verification failed.
       *
       * @type {string}
       */
      var csvFailureMsg;

      /**
       * The completed bulk restore batch for a CSV verification.
       *
       * @type {object}
       */
      var csvCompletedBulkRestoreBatch;

      /**
       * The metadata records being considered for restore. This applies only to MANUAL METADATA and METADATA_COMPARE
       * restores. We don't currently page through metadata records, so this is the whole list.
       *
       * @type {
       *        {
       *         componentType: string,
       *         isExpanded: boolean,
       *         isSelected: boolean,
       *         items: [],
       *         selectedItemCount: number,
       *         total: number
       *        }[]
       *       } An array of objects describing each metadata component type:
       *
       *         componentType:     The name of the component type
       *         isExpanded:        true if this component type is expanded in the view, showing the items
       *         isSelected:        true if this component type is selected (selects all items in this type)
       *         items:             The list of metadata items of this type in the current backup
       *         selectedItemCount: The number of selected items within this type
       *         total:             The number of metadata components of this type
       */
      var metadataRecords = [];

      /**
       * The current page of object records being considered for restore. This applies only to MANUAL OBJECT, BACKUP,
       * and VERIFIED_CSV restores.
       *
       * @type {
       *        {
       *         isDeleted: boolean,
       *         isUndeleted: boolean,
       *         isUpdated: boolean,
       *         sfId: string,
       *         sfName: string
       *        }[]
       *       } An array of objects describing each record
       */
      var pageObjectRecords = [];

      /**
       * The selected object records. This applies only to MANUAL OBJECT, BACKUP, and VERIFIED_CSV restores. Only used
       * if selectingAll is false. We keep these separate so that we don't lose the selection when a new page of
       * records is loaded.
       *
       * @type {Array}
       */
      var selectedObjectRecords = [];

      /**
       * The selected object types object. Represents the object which contains selectedAll flag and selectedList
       * array if selectedAll is false
       *
       * @type {Array}
       */
      let selectedObjectTypes;

      /**
       * Records, selected to include children
       * @type {Array}
       */
      let recordsToIncludeChildren = [];

      /**
       * Map of children for each record on the page
       * @type {Object}
       */
      const childRecords = {};

      /**
       * The total number of object records across all pages.  This applies only to MANUAL OBJECT, BACKUP,
       * and VERIFIED_CSV restores.
       *
       * @type {Object}
       */
      var totalObjectRecordCount = {
        count: 0
      };

      /**
       * Indicates whether the "select all" checkbox is checked. This applies to both the metadata records and the
       * object records.
       *
       * @type {boolean}
       */
      var selectingAll = false;

      /**
       * Selected Object Restore type GRANULAR: 1, CLASSIC: 2.
       *
       * @type {number} an BULK_RESTORE_CHILD_STRATEGIES value
       */
      let objRestoreType = BULK_RESTORE_CHILD_STRATEGIES.CLASSIC;

      /**
       * The number of selected metadata records.
       *
       * @type {number}
       */
      var selectedMetadataCount = 0;

      /**
       * Indicates whether we're including children of the records being restored. For same-org restores we only
       * include children of deleted records. For cross-org restores we include children of all records.
       *
       * @type {boolean}
       */
      var includingChildren = false;

      /**
       * Indicates whether we're repairing lookup relationships.
       *
       * @type {boolean}
       */
      let repairingLookupRelationships = false;

      /**
       * Indicates whether we're restoring lookup relationships.
       *
       * @type {boolean}
       */
      let restoringLookupRelationships = false;

      /**
       * Indicates whether we're disabling salesforce automation.
       *
       * @type {boolean}
       */
      let disableSalesforceAutomation = false;

      /**
       * Indicates whether we're including lookup relationships of the records being restored.
       *
       * @type {boolean}
       */
      let includingLookupRelationships = false;

      /**
       * Indicates whether we're only specifying which fields to restore.
       *
       * @type {boolean}
       */
      var specifyingFields = false;

      /**
       * The entries describing the specific fields being restored. There is one entry per object type. Each entry
       * contains the object type and the field list. Only used if specifyingFields is true.
       *
       * @type {object[]}
       */
      var specificFieldEntries = [];

      /**
       * Indicates whether we're overwriting any values.
       *
       * @type {boolean}
       */
      var overwritingValues = false;

      /**
       * The entries describing the current overwrite values. These may have come from a preset or they may be custom.
       * Only used if overwritingValues is true.
       *
       * @type {object[]}
       */
      var overwriteValueEntries = [];

      /**
       * Indicates whether we're ignore warnings during metadata restore.
       *
       * @type {boolean}
       */
      let ignoreWarnings = false;

      /**
       * This error is thrown if selectingAll is true (we're not maintaining selectedObjectRecords), the caller wants
       * to know something about the selected records, and the current page of records does not include all possible
       * records.
       *
       * @type {string}
       */
      var ALL_RECORDS_NOT_AVAILABLE_MSG = 'Cannot get all selected objects due to paging';

      /**
       * Search records page token
       * @type {string}
       */
      let searchPageToken = null;

      /**
       * Search records page batch id
       * @type {number}
       */
      let searchPageBatchId = null;

      /**
       * Search records token
       * @type {string}
       */
      let searchToken = null;

      let lastSearchParams = null;

      let startSystemModstamp = null;

      let endSystemModstamp = null;

      let sortBy = null;

      let ascending = null;

      let uploadedCsv = null;

      // Initialize and validate the parameters that can be passed on creation
      setRestoreType(aRestoreType || BulkRestoreType.MANUAL);
      setDestType(params && params.destType);
      setRecordType(getRequiredRecordType() || (params && params.recordType));
      setManualObjectListType(params && params.manualObjectListType);
      setSourceOrgInfo(params && params.sourceOrgInfo);
      setDestOrgInfo(params && params.destOrgInfo);
      setBackupBatchId(params && params.backupBatchId);
      setBackupBatchDate(params && params.backupBatchDate);
      setCompareBatchId(params && params.compareBatchId);
      setCompareBatchDate(params && params.compareBatchDate);
      setCsvBatchId(params && params.csvBatchId);
      setCsvCompletedBulkRestoreBatch(params && params.csvCompletedBulkRestoreBatch);
      setCsvDuplicateCount(params && params.csvDuplicateCount);
      setCsvFailed(params && params.csvFailed);
      setCsvFailureMsg(params && params.csvFailureMsg);
      setCsvFileName(params && params.csvFileName);
      setCsvInvalidCount(params && params.csvInvalidCount);
      setCsvObjectTypes(params && params.csvObjectTypes);
      setSearchObjectType(params && params.searchObjectType);
      setSearchObjectStates(params && params.searchObjectStates);

      // Expose the functions only, not the data
      return {
        addOverwriteValueEntry: addOverwriteValueEntry,
        addSpecificFieldEntry: addSpecificFieldEntry,
        canGetSelectedObjectRecords: canGetSelectedObjectRecords,
        clearAllIncludeChildrenWithDescendants,
        clearObjectRecords: clearObjectRecords,
        clearOverwriteValueEntries: clearOverwriteValueEntries,
        clearRecordIncludeChildrenWithDescendants,
        getAscending,
        getSortBy,
        getBackupBatchDate: getBackupBatchDate,
        getBackupBatchId: getBackupBatchId,
        getCsvBatchId: getCsvBatchId,
        getCsvCompletedBulkRestoreBatch: getCsvCompletedBulkRestoreBatch,
        getCsvDuplicateCount: getCsvDuplicateCount,
        getCsvFailureMsg: getCsvFailureMsg,
        getCsvFileName: getCsvFileName,
        getCsvInvalidCount: getCsvInvalidCount,
        getCsvObjectTypes: getCsvObjectTypes,
        getCompareBatchDate: getCompareBatchDate,
        getCompareBatchId: getCompareBatchId,
        getDescription: getDescription,
        getDestOrgAccessTokenOwnerId: getDestOrgAccessTokenOwnerId,
        getDestOrgId: getDestOrgId,
        getDestOrgInfo: getDestOrgInfo,
        getDestOrgName: getDestOrgName,
        getDestOrgSfId: getDestOrgSfId,
        getDestType: getDestType,
        getLastSearchParams,
        getManualObjectListType: getManualObjectListType,
        getMetadataRecords: getMetadataRecords,
        getOverwriteMapForRestore: getOverwriteMapForRestore,
        getOverwriteValueEntries: getOverwriteValueEntries,
        getOverwriteValueEntryCount: getOverwriteValueEntryCount,
        getPageObjectRecordCount: getPageObjectRecordCount,
        getPageObjectRecords: getPageObjectRecords,
        getParamsForExport: getParamsForExport,
        getParamsForRestore: getParamsForRestore,
        getRecordId: getRecordId,
        getRecordName: getRecordName,
        getRecordType: getRecordType,
        getRestoreType: getRestoreType,
        getRequiredRecordType: getRequiredRecordType,
        getSearchEndBatchId: getSearchEndBatchId,
        getSearchObjectStates,
        getSearchObjectType: getSearchObjectType,
        getSearchPageToken,
        getSearchPageBatchId,
        getSearchToken,
        getSearchStartBatchId: getSearchStartBatchId,
        getSearchText: getSearchText,
        getStartSystemModstamp,
        getEndSystemModstamp,
        setUploadedCsv,
        getUploadedCsv,
        getSelectedMetadataRecordCount: getSelectedMetadataRecordCount,
        getSelectedObjectRecordCount: getSelectedObjectRecordCount,
        getSelectedObjectRecordIdentities,
        getSelectedObjectRecordNames: getSelectedObjectRecordNames,
        getSelectedObjectRecords: getSelectedObjectRecords,
        getSelectedObjectRecordsCountInfo,
        getSelectedObjectRecordsPage: getSelectedObjectRecordsPage,
        getSelectedObjectRecordTypes: getSelectedObjectRecordTypes,
        getSelectedObjectTypesList,
        getSourceOrgId: getSourceOrgId,
        getSourceOrgInfo: getSourceOrgInfo,
        getSourceOrgName: getSourceOrgName,
        getSourceOrgSfId: getSourceOrgSfId,
        getSpecificFieldEntries: getSpecificFieldEntries,
        getSpecificFieldEntryCount: getSpecificFieldEntryCount,
        getSpecificFieldsForRestore: getSpecificFieldsForRestore,
        getTotalObjectRecordCount: getTotalObjectRecordCount,
        getUploadFile: getUploadFile,
        getUploadFileName: getUploadFileName,
        hasSelectedDeletedObjects: hasSelectedDeletedObjects,
        hasSelectedUpdatedObjects: hasSelectedUpdatedObjects,
        isCrossOrgRestore: isCrossOrgRestore,
        isCsvFailed: isCsvFailed,
        isIgnoreWarnings,
        isIncludingAllChildren: isIncludingAllChildren,
        isManualObjectSearch: isManualObjectSearch,
        isManualObjectUpload: isManualObjectUpload,
        isManualRestore: isManualRestore,
        isMetadataRestore: isMetadataRestore,
        isObjectRecordSelected: isObjectRecordSelected,
        isObjectRestore: isObjectRestore,
        isOverwritingValues: isOverwritingValues,
        isRecordToIncludeChildren: isRecordToIncludeChildren,
        isRestoreFromBackup: isRestoreFromBackup,
        isRestoreFromCsv: isRestoreFromCsv,
        isRestoreFromMetadataCompare: isRestoreFromMetadataCompare,
        isSameOrgRestore: isSameOrgRestore,
        isSelectedAtLeastOneRecord,
        isSelectingAll: isSelectingAll,
        isSpecifyingFields: isSpecifyingFields,
        overwriteValuesAreValid: overwriteValuesAreValid,
        removeOverwriteValueEntry: removeOverwriteValueEntry,
        removeSpecificFieldEntry: removeSpecificFieldEntry,
        requiresBackupBatch: requiresBackupBatch,
        requiresCompareBatch: requiresCompareBatch,
        requiresSearchObjectStates,
        requiresSearchObjectType: requiresSearchObjectType,
        requiresTotalObjectRecordCount: requiresTotalObjectRecordCount,
        setBackupBatchDate: setBackupBatchDate,
        setBackupBatchId: setBackupBatchId,
        resetBackupBatchId: resetBackupBatchId,
        setAscending,
        setSortBy,
        setCompareBatchDate: setCompareBatchDate,
        setCompareBatchId: setCompareBatchId,
        setCsvBatchId: setCsvBatchId,
        setCsvCompletedBulkRestoreBatch: setCsvCompletedBulkRestoreBatch,
        setCsvDuplicateCount: setCsvDuplicateCount,
        setCsvFailed: setCsvFailed,
        setCsvFailureMsg: setCsvFailureMsg,
        setCsvFileName: setCsvFileName,
        setCsvInvalidCount: setCsvInvalidCount,
        setCsvObjectTypes: setCsvObjectTypes,
        setDestOrgInfo: setDestOrgInfo,
        setDestType: setDestType,
        setIgnoreWarnings,
        setIncludingChildren: setIncludingChildren,
        setLastSearchParams,
        setManualObjectListType: setManualObjectListType,
        setMetadataRecords: setMetadataRecords,
        setPageObjectRecords: setPageObjectRecords,
        setOverwritingValues: setOverwritingValues,
        setRecordType: setRecordType,
        setSearchEndBatchId: setSearchEndBatchId,
        setSearchObjectStates,
        setSearchObjectType: setSearchObjectType,
        setSearchPageToken,
        setSearchPageBatchId,
        setSearchToken,
        setSearchStartBatchId: setSearchStartBatchId,
        setSearchText: setSearchText,
        setStartSystemModstamp,
        setEndSystemModstamp,
        getSelectedObjectTypes,
        setSelectedObjectTypes,
        checkObjectTypeSelectedCount,
        setSelectedMetadataRecordCount: setSelectedMetadataRecordCount,
        setSelectingAll: setSelectingAll,
        setSourceOrgInfo: setSourceOrgInfo,
        setSpecifyingFields: setSpecifyingFields,
        setTotalObjectRecordCount: setTotalObjectRecordCount,
        setUploadFile: setUploadFile,
        specificFieldsAreValid: specificFieldsAreValid,
        toggleObjectRecordSelection: toggleObjectRecordSelection,
        usesBackupBatch: usesBackupBatch,
        usesCompareBatch: usesCompareBatch,
        usesSearchObjectStates,
        usesSearchObjectType: usesSearchObjectType,
        usesTotalObjectRecordCount: usesTotalObjectRecordCount,
        isManualObjectBrowse,
        ALL_RECORDS_NOT_AVAILABLE_MSG: ALL_RECORDS_NOT_AVAILABLE_MSG,
        childRecords,
        getObjectRestoreType,
        setObjectRestoreType,
        setIncludingLookup,
        isIncludingLookup,
        transformPlateIntoTree,
        resetIncludeChildrenFlagForChildren,
        clearAllSelections,
        isIncludingAnyChildren,
        pushRecordToIncludeChildren,
        removeRecordFromIncludeChildrenList,
        checkAndMarkRecordsAreIncludingChildren,
        hasPartialSelectionOfChildren,
        isRepairingLookupRelationships,
        setRepairingLookupRelationships,
        isRestoringLookupRelationships,
        setRestoringLookupRelationships,
        isDisablingSalesforceAutomation,
        setDisableSalesforceAutomation
      };

      /**
       * Adds an overwrite value entry.
       *
       * @param {object} [entry] - the entry to be added. If not present a new entry is created and added.
       * @returns {object} the new overwrite value entry.
       */
      function addOverwriteValueEntry(entry) {
        entry = entry || {
          objectType: null,
          objectField: null,
          value: null,
          strategy: RESTORE_OVERWRITE_STRATEGIES.CONSTANT
        };

        overwriteValueEntries.push(entry);

        return entry;
      }

      /**
       * Adds a new, uninitialized specific field entry.
       *
       * @param {object} [entry] - the entry to be added. If not present a new entry is created and added.
       * @returns {object} the new specific field entry.
       */
      function addSpecificFieldEntry(entry) {
        entry = entry || {
          objectType: null,
          objectFields: []
        };

        specificFieldEntries.push(entry);

        return entry;
      }

      /**
       * Determines whether we know all selected object records. This is only an issue when selectingAll is true (i.e.
       * we're not using selectedObjectRecords) and the current page of records does not contain all records.
       *
       * @returns {boolean} true if any call that requires knowledge of all selected records will succeed.
       */
      function canGetSelectedObjectRecords() {
        // If we're not selectingAll, the list of selected records is available in selectedObjectRecords
        // Otherwise, if the number of records in pageRecordObjects list is the total number of records we're good.
        return !selectingAll || getPageObjectRecordCount() === totalObjectRecordCount.count;
      }

      /**
       * Clears the current page and selected list of object records.
       */
      function clearObjectRecords() {
        pageObjectRecords = [];
        totalObjectRecordCount = {
          count: 0,
          threshold: 0,
          thresholdAchieved: false,
          approximate: false
        };

        clearSelectedObjectRecords();
      }

      /**
       * Clears the selected list of object records
       */
      function clearSelectedObjectRecords() {
        selectedObjectRecords = [];
      }

      /**
       * Clears the list of overwrite value entries.
       */
      function clearOverwriteValueEntries() {
        overwriteValueEntries = [];
      }

      /**
       * Returns an ISO 8601 string representing the date of the selected backup for MANUAL OBJECT UPLOAD restores,
       * MANUAL METADATA restores, and BACKUP restores. Returns the date of the "from" backup for METADATA_COMPARE
       * restores.
       *
       * @returns {string} an ISO 8601 string representing the date of the backup batch.
       */
      function getBackupBatchDate() {
        return backupBatchDate;
      }

      /**
       * Returns the id of the selected backup for MANUAL OBJECT UPLOAD restores, MANUAL METADATA restores, and BACKUP
       * restores. Returns the id of the "from" backup for METADATA_COMPARE restores.
       *
       * @returns {number} the id of the backup batch.
       */
      function getBackupBatchId() {
        return backupBatchId;
      }

      /**
       * Returns the id of the batch in which the CSV was verified for VERIFIED_CSV restores.
       *
       * @returns {number} the id of the CSV verification batch.
       */
      function getCsvBatchId() {
        return csvBatchId;
      }

      /**
       * Returns the completed bulk restore batch for a CSV verification.
       *
       * @returns {Object} the completed bulk restore batch
       */
      function getCsvCompletedBulkRestoreBatch() {
        return csvCompletedBulkRestoreBatch;
      }

      /**
       * Returns the number of duplicate records found during verification for VERIFIED_CSV restores.
       *
       * @returns {number} the number of duplicate records found.
       */
      function getCsvDuplicateCount() {
        return csvDuplicateCount;
      }

      /**
       * Returns the failure message for VERIFIED_CSV restores if the verification failed.
       *
       * @returns {string} the verification failure message.
       */
      function getCsvFailureMsg() {
        return csvFailureMsg;
      }

      /**
       * Returns the uploaded CSV file for VERIFIED_CSV restores.
       *
       * @returns {string} the CSV file name.
       */
      function getCsvFileName() {
        return csvFileName;
      }

      /**
       * Returns the number of invalid records found during verification for VERIFIED_CSV restores.
       *
       * @returns {number} the number of invalid records found.
       */
      function getCsvInvalidCount() {
        return csvInvalidCount;
      }

      /**
       * Returns the Salesforce object types for VERIFIED_CSV restores.
       *
       * @returns {string[]} the list of object types.
       */
      function getCsvObjectTypes() {
        return csvObjectTypes;
      }

      /**
       * Returns an ISO 8601 string representing the date of the "compare against" backup for METADATA_COMPARE restores.
       *
       * @returns {string} an ISO 8601 string representing the date of the "compare against" backup.
       */
      function getCompareBatchDate() {
        return compareBatchDate;
      }

      /**
       * Returns the id of the "compare against" backup for METADATA_COMPARE restores.
       *
       * @returns {number} the id of the "compare against" backup.
       */
      function getCompareBatchId() {
        return compareBatchId;
      }

      /**
       * Returns a reasonable description of the type of restore being configured.
       *
       * @returns {string} the description.
       */
      function getDescription() {
        var desc;

        switch (destType) {
          case BULK_RESTORE_DEST_TYPES.CROSS_ORG:
            desc = 'Cross-org';
            break;

          case BULK_RESTORE_DEST_TYPES.SAME_ORG:
            desc = 'Same-org';
            break;

          default:
            desc = '(unknown destType)';
            break;
        }

        switch (recordType) {
          case BULK_RESTORE_TYPES.METADATA:
            if (restoreType !== BulkRestoreType.METADATA_COMPARE) {
              desc += ' metadata';
            }
            break;

          case BULK_RESTORE_TYPES.OBJECT:
            desc += ' object';
            break;

          default:
            desc += ' (unknown recordType)';
            break;
        }

        switch (restoreType) {
          case BulkRestoreType.BACKUP:
            desc += ' restore from backup';
            break;

          case BulkRestoreType.METADATA_COMPARE:
            desc += ' restore from metadata compare';
            break;

          case BulkRestoreType.VERIFIED_CSV:
            desc += ' restore from verified CSV';
            break;

          case BulkRestoreType.MANUAL:
            switch (manualObjectListType) {
              case BulkRestoreObjectListType.UPLOAD:
                desc += ' CSV upload';
                break;

              case BulkRestoreObjectListType.SEARCH:
              default:
                desc += ' search';
                break;
            }
            break;

          default:
            desc += ' (unknown restoreType)';
            break;
        }

        return desc;
      }

      /**
       * Returns the access token owner id for the org that is the destination of the restore data.
       *
       * @returns {number} the access token owner id for the destination org.
       */
      function getDestOrgAccessTokenOwnerId() {
        return destOrgInfo && destOrgInfo.accessTokenOwnerId;
      }

      /**
       * Returns the Spanning id for the org that is the destination of the restore data.
       *
       * @returns {number} the Spanning id for the destination org.
       */
      function getDestOrgId() {
        if (isCrossOrgRestore()) {
          return destOrgInfo && destOrgInfo.id;
        } else {
          return getSourceOrgId();
        }
      }

      /**
       * Returns the info for the org that is the destination of the restore data.
       *
       * @returns {{id: number, name: string, sfId: string, accessTokenOwnerId: string}} the info for the destination
       *   org.
       */
      function getDestOrgInfo() {
        return destOrgInfo;
      }

      /**
       * Returns the name of the destination org.
       *
       * @returns {string} the name of the destination org.
       */
      function getDestOrgName() {
        if (isCrossOrgRestore()) {
          return destOrgInfo && destOrgInfo.name;
        } else {
          return getSourceOrgName();
        }
      }

      /**
       * Returns the Salesforce id of the destination org.
       *
       * @returns {string} the Salesforce id of the destination org.
       */
      function getDestOrgSfId() {
        if (isCrossOrgRestore()) {
          return destOrgInfo && destOrgInfo.sfId;
        } else {
          return getSourceOrgSfId();
        }
      }

      /**
       * Returns the destination type, either same org or cross org.
       *
       * @returns {number} the destination type.
       */
      function getDestType() {
        return destType;
      }

      /**
       * Returns the last search parameters object
       * @returns {null}
       */
      function getLastSearchParams() {
        return lastSearchParams;
      }

      /**
       * Returns the ascending
       */
      function getAscending() {
        return ascending;
      }

      /**
       * Returns the sort by field name
       */
      function getSortBy() {
        return sortBy;
      }

      /**
       * Returns the object list type type.
       *
       * @returns {string} the object list type.
       */
      function getManualObjectListType() {
        return manualObjectListType;
      }

      /**
       * Returns the metadata records.
       *
       * @returns {object[]} the metadata records.
       */
      function getMetadataRecords() {
        return metadataRecords;
      }

      /**
       * Builds the final overwrite map for use in a bulk restore.
       *
       * @returns {Object.<string, Object.<string, {strategy: string, value: string}>> | undefined} the overwrite map
       */
      function getOverwriteMapForRestore() {
        if (!overwritingValues) {
          return;
        }

        var overwriteMap = {};

        // We need to create the object the backend expects for overwrite map
        _.each(overwriteValueEntries, function (ovEntry) {
          // Get or create the map value for this object type
          var objectTypeOverwrites = overwriteMap[ovEntry.objectType] || {};

          // Add this field
          objectTypeOverwrites[ovEntry.objectField] = {
            strategy: ovEntry.strategy,
            value: ovEntry.value
          };

          // Save the updated map value in case it's new
          overwriteMap[ovEntry.objectType] = objectTypeOverwrites;
        });

        return overwriteMap;
      }

      /**
       * Returns the entries describing the overwrite values.
       *
       * @returns {object[]} the array of overwrite value entries.
       */
      function getOverwriteValueEntries() {
        return overwriteValueEntries;
      }

      /**
       * Returns the number of configured overwrite value entries.
       *
       * @returns {number} the number of configured overwrite value entries.
       */
      function getOverwriteValueEntryCount() {
        return _.size(overwriteValueEntries);
      }

      /**
       * Returns the number of object records.
       *
       * @returns {number} the number of object records.
       */
      function getPageObjectRecordCount() {
        return _.size(pageObjectRecords);
      }

      /**
       * Returns the object records for the current page of results.
       *
       * @returns {object[]} the object records for the current page of results.
       */
      function getPageObjectRecords() {
        return pageObjectRecords;
      }

      /**
       * Builds a params object for use in submitting a bulk restore preview/export.
       *
       * @returns {{
       *            csvVerified: boolean,
       *            filter: string,
       *            includeChildren: boolean,
       *            repairRelationships: boolean,
       *            sobjectName: string,
       *            sobjectStates: number[],
       *            restoreType: number,
       *            targetOrgId: number,
       *            totalObjects: number
       *           }} the params object
       */
      function getParamsForExport() {
        const getFromLastSearchParams = !!(lastSearchParams && !selectedObjectTypes);
        const params = {
          csvVerified: isRestoreFromCsv(),
          filter: getFromLastSearchParams ? lastSearchParams.searchText : searchText,
          startSystemModstamp: getFromLastSearchParams ? lastSearchParams.startSystemModstamp : startSystemModstamp,
          endSystemModstamp: getFromLastSearchParams ? lastSearchParams.endSystemModstamp : endSystemModstamp,
          includeChildren: includingChildren,
          includeLookup: includingLookupRelationships,
          sobjectName: getFromLastSearchParams ? lastSearchParams.objectType : searchObjectType,
          sobjectStates: getFromLastSearchParams ? lastSearchParams.objectStates : searchObjectStates,
          restoreType: objRestoreType,
          targetOrgId: getDestOrgId(),
          totalObjects: getSelectedObjectRecordCount(),
          searchMode: isManualObjectBrowse() ? 'BROWSE' : 'INTERVAL'
        };

        if (isRestoreFromBackup()) {
          params.batchId = backupBatchId;
        } else if (isRestoreFromCsv()) {
          params.batchId = csvBatchId;
        } else {
          params.start = getFromLastSearchParams ? lastSearchParams.startBatchId : searchStartBatchId;
          params.end = getFromLastSearchParams ? lastSearchParams.endBatchId : searchEndBatchId;
        }

        if (selectingAll) {
          params.items = 'ALL';
        } else if (getSelectedObjectRecordCount() > 0) {
          params.items = JSON.stringify({
            items: getSelectedObjectRecordIdentities()
          });
          if (objRestoreType === BULK_RESTORE_CHILD_STRATEGIES.GRANULAR) {
            params.restoreChildrenMap = buildRestoreChildrenMap(getSelectedObjectRecordIdentities());
          }
        } else if (selectedObjectTypes) {
          params.selectedObjectTypes = {
            selectedAll: selectedObjectTypes.selectedAll,
            selectedList: selectedObjectTypes.selectedAll ? null : selectedObjectTypes.selectedList
          };
        } else {
          throw new Error('No items selected for restore');
        }
        return params;
      }

      /**
       * Builds restoreChildrenMap
       *
       * @param {id: number, batchId: number} identities
       */
      function buildRestoreChildrenMap(identities) {
        const restoreChildrenMap = {};
        identities.forEach((identity) => {
          if (childRecords[identity.id]) {
            restoreChildrenMap[identity.id] = getSelectedChildren(childRecords[identity.id]) || {};
          } else {
            restoreChildrenMap[identity.id] = {};
          }
          const selectedObj = selectedObjectRecords.find(
            (record) => record.recordId === identity.id && record.batchId === identity.batchId
          );
          if (selectedObj) {
            restoreChildrenMap[identity.id].includeChildren = !!selectedObj.includeChildren;
          }
          restoreChildrenMap[identity.id].batchId = identity.batchId;
        });

        return restoreChildrenMap;
      }

      /**
       * Returns children tree
       *
       * @param {object[]} records
       */
      function getSelectedChildren(records) {
        let selectedChildren;
        records
          .filter(({ isSelected }) => isSelected)
          .forEach(({ id, children, includeChildren }) => {
            if (!selectedChildren) {
              selectedChildren = getDefaultRestoreChildrenMap(true);
            }
            selectedChildren.items.push(id);

            const result = getSelectedChildren(children);

            if (result) {
              selectedChildren.restoreChildrenMap[id] = result;
              selectedChildren.includeChildren = true;
            } else {
              selectedChildren.restoreChildrenMap[id] = getDefaultRestoreChildrenMap(includeChildren);
            }
          });

        return selectedChildren;
      }

      /**
       * Returns default restore children map structure
       *
       * @param includeChildren boolean to define the default value for this field
       * @returns {{includeChildren: *, items: Array, restoreChildrenMap: {}}}
       */
      function getDefaultRestoreChildrenMap(includeChildren) {
        return {
          items: [],
          restoreChildrenMap: {},
          includeChildren
        };
      }

      /**
       * Transforms plate response from backend into tree.
       * As a result, each record will have 'children' array.
       *
       * @param {Array} records
       * @return {Array}
       */
      function transformPlateIntoTree(records) {
        const recordsCopy = [...records];
        for (const [i, record] of recordsCopy.entries()) {
          const childrenLength = recordsCopy.slice(i + 1).findIndex(({ depth }) => depth === record.depth);
          let children;
          if (childrenLength > 0) {
            children = recordsCopy.splice(i + 1, childrenLength); // get children
          } else if (childrenLength === 0) {
            children = []; // no children
          } else {
            children = recordsCopy.splice(i + 1); // all elements are children
          }
          record.children = transformPlateIntoTree(children);
          record.isSelected = false;
        }

        return recordsCopy;
      }

      /**
       * Builds a params object for use in submitting a bulk restore.
       *
       * @returns {{
       *            csvVerifiedRestore: boolean,
       *            filter: string,
       *            includeChildren: boolean,
       *            overwriteMap: object,
       *            sobjectName: string,
       *            sobjectStates: number[],
       *            specificFields: object,
       *            targetOrgId: number,
       *            totalObjects: number
       *           }} the params object
       */
      function getParamsForRestore() {
        let lookupRelationships;
        if (isRestoringLookupRelationships()) {
          lookupRelationships = RESTORE_LOOKUP_RELATIONSHIP_ENUM.Restore;
        } else if (isRepairingLookupRelationships()) {
          lookupRelationships = RESTORE_LOOKUP_RELATIONSHIP_ENUM.Repair;
        } else {
          lookupRelationships = RESTORE_LOOKUP_RELATIONSHIP_ENUM.NoAction;
        }
        const params = _.extend(getParamsForExport(), {
          overwriteMap: getOverwriteMapForRestore(),
          specificFields: getSpecificFieldsForRestore(),
          lookupRelationships,
          disableSalesforceAutomation: isDisablingSalesforceAutomation()
        });

        // Our export and restore endpoints look for this setting under different names, so we can't totally share the
        // export params with restore
        params.csvVerifiedRestore = params.csvVerified;
        delete params.csvVerified;

        if (isCrossOrgRestore()) {
          params.accessTokenOwnerId = getDestOrgAccessTokenOwnerId();
        }

        return params;
      }

      /**
       * Returns the id for the specified record.
       *
       * @param {{[recordId]: number, [id]: number}} record - the record whose id is to be returned.
       * @returns {number} the id of the record.
       */
      function getRecordId(record) {
        const id = record.recordId || record.id;

        // This check is primarily to help during testing to make sure the dummy data is set up correctly
        if (!id) {
          throw new Error('Found record with no id: ' + JSON.stringify(record));
        }

        return id;
      }

      /**
       * Returns the identity for the specified record.
       *
       * @param record - the record whose identity is to be returned.
       * @returns { id: number, batchId: number } the identity of the record.
       */
      function getRecordIdentity(record) {
        const id = getRecordId(record);
        const batchId =
          record.csvVerifyId && record.sobjectRecordBatchId ? record.sobjectRecordBatchId : record.batchId;
        return { id, batchId };
      }

      /**
       * Returns the name for the specified record.
       *
       * @param {{[sfName]: string}} record - the record whose name is to be returned.
       * @returns {string} the name of the record.
       */
      function getRecordName(record) {
        return record.sfName || '';
      }

      /**
       * Returns the record type.
       *
       * @returns {number} the record type.
       */
      function getRecordType() {
        return recordType;
      }

      /**
       * Returns the type of restore being performed.
       *
       * @returns {string} a BulkRestoreType value.
       */
      function getRestoreType() {
        return restoreType;
      }

      /**
       * Determines whether the current restore type has a required record type.
       *
       * @returns {number|undefined} the required record type, if any, for the current restore type.
       */
      function getRequiredRecordType() {
        // Some restore types have a known record type
        switch (restoreType) {
          case BulkRestoreType.BACKUP:
          case BulkRestoreType.VERIFIED_CSV:
            return BULK_RESTORE_TYPES.OBJECT;

          case BulkRestoreType.METADATA_COMPARE:
            return BULK_RESTORE_TYPES.METADATA;

          case BulkRestoreType.MANUAL:
          default:
            // The user will decide
            break;
        }

        return undefined;
      }

      /**
       * Returns the "before" backup for MANUAL OBJECT SEARCH restores.
       *
       * @returns {number} the "before" backup batch id.
       */
      function getSearchEndBatchId() {
        return searchEndBatchId;
      }

      /**
       * Returns the updated/deleted/new/unchanged selection for MANUAL OBJECT SEARCH restores.
       *
       * @returns {number} the SOBJECT_STATE.
       */
      function getSearchObjectStates() {
        return searchObjectStates;
      }

      /**
       * Returns the selected Salesforce object type for MANUAL OBJECT SEARCH restores.
       *
       * @returns {string} the object type.
       */
      function getSearchObjectType() {
        return searchObjectType;
      }

      /**
       * Returns the search page token.
       *
       * @returns {string} the search page token.
       */
      function getSearchPageToken() {
        return searchPageToken;
      }

      /**
       * Returns the search page batch id.
       *
       * @returns {number} the search page batch id.
       */
      function getSearchPageBatchId() {
        return searchPageBatchId;
      }

      /**
       * Returns the search token.
       *
       * @returns {string} the search token.
       */
      function getSearchToken() {
        return searchToken;
      }

      /**
       * Returns the "from" backup for MANUAL OBJECT SEARCH restores.
       *
       * @returns {number} the "from" backup batch id.
       */
      function getSearchStartBatchId() {
        return searchStartBatchId;
      }

      /**
       * Returns the "search object name" string for MANUAL OBJECT SEARCH restores.
       *
       * @returns {string} the search text.
       */
      function getSearchText() {
        return searchText;
      }

      /**
       * Get start systemModstamp
       * @returns {null}
       */
      function getStartSystemModstamp() {
        return startSystemModstamp;
      }

      /**
       * Get end systemModstamp
       * @returns {null}
       */
      function getEndSystemModstamp() {
        return endSystemModstamp;
      }

      /**
       * Get uploaded csv info
       * @returns {null}
       */
      function getUploadedCsv() {
        return uploadedCsv;
      }

      /**
       * Set uploaded csv info
       * @returns {null}
       */
      function setUploadedCsv(value) {
        uploadedCsv = value;
      }

      /**
       * Returns the number of selected metadata or object records.
       *
       * @returns {number} the number of selected records.
       */
      function getSelectedMetadataRecordCount() {
        return selectedMetadataCount;
      }

      /**
       * Returns the number of selected object records.
       *
       * @returns {number} the number of selected records.
       */
      function getSelectedObjectRecordCount() {
        return selectingAll ? totalObjectRecordCount.count : _.size(selectedObjectRecords);
      }

      /**
       * Determines if at least 1 record was selected
       * @returns {boolean}
       */
      function isSelectedAtLeastOneRecord() {
        return selectingAll ? _.size(pageObjectRecords) > 0 : _.size(selectedObjectRecords) > 0;
      }

      /**
       * Returns a list of the ids of all selected object records.
       *
       * @returns {number[]} the ids of all selected object records.
       */
      function getSelectedObjectRecordIdentities() {
        return _.map(getSelectedObjectRecords(), getRecordIdentity);
      }

      /**
       * Returns a list of the names of all selected object records.
       *
       * @returns {string[]} the names of all selected object records.
       */
      function getSelectedObjectRecordNames() {
        return _.map(getSelectedObjectRecords(), getRecordName);
      }

      /**
       * Returns the list of selected object records.
       *
       * @param {boolean} ignoreSelectingAllFlag - get selected object records ignoring selectingAll flag
       * @returns {object[]} The list of records.
       */
      function getSelectedObjectRecords(ignoreSelectingAllFlag = false) {
        if (!ignoreSelectingAllFlag && !canGetSelectedObjectRecords()) {
          throw new Error(ALL_RECORDS_NOT_AVAILABLE_MSG);
        }

        return !ignoreSelectingAllFlag && selectingAll ? pageObjectRecords : selectedObjectRecords;
      }

      /**
       * Returns the info (count and message) of selected items. P.s. if selectedAll flag is true and count more than
       * threshold, it will return count value from the server. It is minimum existing records, not exact value.
       * @returns { count: number; text: string; }
       */
      function getSelectedObjectRecordsCountInfo() {
        const length = selectingAll ? totalObjectRecordCount.count : selectedObjectRecords.length;

        const i18n = $filter('i18n');
        let totalCountMessage = totalObjectRecordCount.approximate ? `${i18n('ABOUT')} ` : '';
        if (!selectingAll) {
          totalCountMessage = length;
        } else if (totalObjectRecordCount.thresholdAchieved) {
          totalCountMessage += `${totalObjectRecordCount.threshold}+`;
        } else {
          totalCountMessage += totalObjectRecordCount.count;
        }

        return {
          count: length,
          text: totalCountMessage
        };
      }

      /**
       * Returns a page of selected object records.
       *
       * @param {object} pagingInfo - Specifies the desired page.
       * @returns {object[]} The list of records for the specified page.
       */
      function getSelectedObjectRecordsPage(pagingInfo) {
        // get all the selected records, sort the entire list, and then return the correct set.
        // NOTE that we can't reliably sort by id/recordId since we apparently don't know which type of id will be
        // present (see getRecordId())
        var records = $filter('orderBy')(getSelectedObjectRecords(), pagingInfo.sortBy, !pagingInfo.ascending);
        var begin = (pagingInfo.currentPage - 1) * pagingInfo.itemsPerPage;
        var end = begin + pagingInfo.itemsPerPage;
        return records.slice(begin, end);
      }

      /**
       * Returns a list of the types of all selected objects or records' objects. Each type appears in the list only once.
       *
       * @returns {string[]} the types of all selected objects or records' objects.
       */
      function getSelectedObjectTypesList() {
        if (selectedObjectTypes) {
          return [...selectedObjectTypes.selectedList];
        } else {
          return getSelectedObjectRecordTypes();
        }
      }

      /**
       * Returns a list of the types of all selected object records. Each type appears in the list only once.
       *
       * @returns {string[]} the types of all selected object records.
       */
      function getSelectedObjectRecordTypes() {
        // Restore from CSV can have more than one object type.
        if (isRestoreFromCsv()) {
          // If the user is selecting all records we can just use the full list of types
          if (isSelectingAll()) {
            return getCsvObjectTypes();
          }

          // If the user is not selecting all records we have to look at each selected record
          const map = {};

          _.each(getSelectedObjectRecords(), function (record) {
            // Record the type in the map. The value is irrelevant.
            map[record.sobjectType] = true;
          });

          return _.keys(map);
        } else {
          return [getSearchObjectType()];
        }
      }

      /**
       * Returns the Spanning id for the org that is the source of the restore data.
       *
       * @returns {number} the Spanning id for the source org.
       */
      function getSourceOrgId() {
        return sourceOrgInfo && sourceOrgInfo.id;
      }

      /**
       * Returns the info for the org that is the source of the restore data.
       *
       * @returns {{id: number, name: string, sfId: string}} the info for the source org.
       */
      function getSourceOrgInfo() {
        return sourceOrgInfo;
      }

      /**
       * Returns the name of the org that is the source of the restore data.
       *
       * @returns {string} the name of the source org.
       */
      function getSourceOrgName() {
        return sourceOrgInfo && sourceOrgInfo.name;
      }

      /**
       * Returns the Salesforce id for the org that is the source of the restore data.
       *
       * @returns {string} the Salesforce id for the source org.
       */
      function getSourceOrgSfId() {
        return sourceOrgInfo && sourceOrgInfo.sfId;
      }

      /**
       * Returns the entries describing the specific fields to be restored.
       *
       * @returns {object[]} the array of specific field entries. Each entry contains the object type and a list of
       * fields.
       */
      function getSpecificFieldEntries() {
        return specificFieldEntries;
      }

      /**
       * Returns the number of configured specific field entries.
       *
       * @returns {number} the number of configured specific field entries. This is the number of object types, not the
       * total number of fields.
       */
      function getSpecificFieldEntryCount() {
        return _.size(specificFieldEntries);
      }

      /**
       * Builds the final specific fields data structure for use in a bulk restore.
       *
       * @returns {Object.<string, string[]> | undefined} the specific fields map
       */
      function getSpecificFieldsForRestore() {
        if (!specifyingFields) {
          return;
        }

        const specificFieldsMap = {};

        _.each(specificFieldEntries, function (sfEntry) {
          specificFieldsMap[sfEntry.objectType] = sfEntry.objectFields;
        });

        return specificFieldsMap;
      }

      /**
       * Returns the total number of object records.
       *
       * @returns {Object} the total number of object records.
       */
      function getTotalObjectRecordCount() {
        return totalObjectRecordCount;
      }

      /**
       * Returns the selected CSV file for MANUAL OBJECT UPLOAD restores.
       *
       * @returns {object} the CSV file.
       */
      function getUploadFile() {
        return uploadFile;
      }

      /**
       * Returns the name of the selected CSV file for MANUAL OBJECT UPLOAD restores.
       *
       * @returns {string} the name of the CSV file.
       */
      function getUploadFileName() {
        return uploadFile && uploadFile.name;
      }

      /**
       * Determines whether there are any deleted records in the selected object records.
       *
       * @returns {boolean} true if there are any deleted records selected.
       */
      function hasSelectedDeletedObjects() {
        if (searchObjectStates.indexOf(SOBJECT_STATE.DELETED) >= 0) {
          // All records are deleted, so we need at least one selected.
          return getSelectedObjectRecordCount() > 0;
        } else {
          return false;
        }
      }

      /**
       * Determines whether there are any updated records in the selected object records.
       *
       * @returns {boolean} true if there are any updated records selected.
       */
      function hasSelectedUpdatedObjects() {
        if (searchObjectStates.indexOf(SOBJECT_STATE.UPDATED) >= 0) {
          // All records are updated, so we need at least one selected.
          return getSelectedObjectRecordCount() > 0;
        } else {
          return false;
        }
      }

      /**
       * Determines whether we're configuring a cross-org restore.
       *
       * @returns {boolean} true if we're configuring a cross-org restore.
       */
      function isCrossOrgRestore() {
        return destType === BULK_RESTORE_DEST_TYPES.CROSS_ORG;
      }

      /**
       * Determines whether the CSV verification failed.
       *
       * @returns {boolean} true if the verification failed.
       */
      function isCsvFailed() {
        return csvFailed;
      }

      /**
       * Determines whether we're ignore warnings during metadata restore.
       *
       * @return {boolean} - true if we're ignore warnings during metadata restore.
       */
      function isIgnoreWarnings() {
        return ignoreWarnings;
      }

      /**
       * Determines whether we're including children of the records being restored. For same-org restores we only
       * include children of deleted records. For cross-org restores we include children of all records.
       *
       * @returns {boolean} true if we're including children.
       */
      function isIncludingAllChildren() {
        return includingChildren;
      }

      /**
       * Determines whether we're repairing lookup relationships in this restore.
       *
       * @returns {boolean} true if we're repairing lookup relationships.
       */
      function isRepairingLookupRelationships() {
        return repairingLookupRelationships || restoringLookupRelationships;
      }

      /**
       * Determines whether we're restoring lookup relationships in this restore.
       *
       * @returns {boolean} true if we're restoring lookup relationships.
       */
      function isRestoringLookupRelationships() {
        return restoringLookupRelationships;
      }

      /**
       * Determines whether we're disabling salesforce automation in this restore.
       *
       * @returns {boolean} true if we're disabling salesforce automation.
       */
      function isDisablingSalesforceAutomation() {
        return disableSalesforceAutomation;
      }

      /**
       * Determines whether we're including children of the records being restored. For same-org restores we only
       * include children of deleted records.
       *
       * @returns {boolean} true if we're including children at least for one record.
       */
      function isIncludingAnyChildren() {
        if (isIncludingAllChildren()) {
          return true;
        }

        if (isSelectingAll()) {
          return recordsToIncludeChildren.length > 0;
        }

        return getSelectedObjectRecords().some((record) => record.includeChildren);
      }

      /**
       * Determines whether the user is configuring a MANUAL OBJECT SEARCH restore.
       *
       * @returns {boolean} true if the user is configuring a MANUAL OBJECT SEARCH restore.
       */
      function isManualObjectSearch() {
        return isManualRestore() && isObjectRestore() && manualObjectListType === BulkRestoreObjectListType.SEARCH;
      }

      /**
       * Determines whether the user is configuring a MANUAL OBJECT BROWSE SEARCH restore.
       *
       * @returns {boolean} true if the user is configuring a MANUAL OBJECT BROWSE SEARCH restore.
       */
      function isManualObjectBrowse() {
        return isManualRestore() && isObjectRestore() && manualObjectListType === BulkRestoreObjectListType.BROWSE;
      }

      /**
       * Determines whether the user is configuring a MANUAL OBJECT UPLOAD restore.
       *
       * @returns {boolean} true if the user is configuring a MANUAL OBJECT UPLOAD restore.
       */
      function isManualObjectUpload() {
        return isManualRestore() && isObjectRestore() && manualObjectListType === BulkRestoreObjectListType.UPLOAD;
      }

      /**
       * Determines whether the user is manually configuring the restore.
       *
       * @returns {boolean} true if the user is manually configuring the restore.
       */
      function isManualRestore() {
        return restoreType === BulkRestoreType.MANUAL;
      }

      /**
       * Determines whether the user is currently configuring a metadata restore.
       *
       * @returns {boolean} true if the user is configuring a metadata restore.
       */
      function isMetadataRestore() {
        return recordType === BULK_RESTORE_TYPES.METADATA;
      }

      /**
       * Determines whether an object record is selected.
       *
       * @param {object} record - the record to be checked.
       * @param {boolean} ignoreSelectingAllFlag - check if object record is selected ignoring selectingAllFlag
       * @returns {boolean} true if the specified record is selected.
       */
      function isObjectRecordSelected(record, ignoreSelectingAllFlag = false) {
        if (selectingAll && !ignoreSelectingAllFlag) {
          return true;
        }

        const id = getRecordId(record);
        return !!_.find(getSelectedObjectRecords(ignoreSelectingAllFlag), function (selectedRecord) {
          return getRecordId(selectedRecord) === id;
        });
      }

      /**
       * Determines whether the user is currently configuring an object restore.
       *
       * @returns {boolean} true if the user is configuring an object restore.
       */
      function isObjectRestore() {
        return recordType === BULK_RESTORE_TYPES.OBJECT;
      }

      /**
       * Determines whether we're overwriting any values.
       *
       * @returns {boolean} true if we're overwriting any values.
       */
      function isOverwritingValues() {
        return overwritingValues;
      }

      /**
       * Check if record is to include children
       *
       * @param {object} record
       */
      function isRecordToIncludeChildren(record) {
        return recordsToIncludeChildren.some((rec) => rec === record);
      }

      /**
       * Determines whether the user is configuring a restore from a specific backup.
       *
       * @returns {boolean} true if the user is configuring a restore from a specific backup.
       */
      function isRestoreFromBackup() {
        return restoreType === BulkRestoreType.BACKUP;
      }

      /**
       * Determines whether the user is configuring a restore from a verified CSV upload.
       *
       * @returns {boolean} true if the user is configuring a restore from a verified CSV upload.
       */
      function isRestoreFromCsv() {
        return restoreType === BulkRestoreType.VERIFIED_CSV;
      }

      /**
       * Determines whether the user is configuring a restore from a metadata comparison.
       *
       * @returns {boolean} true if the user is configuring a restore from a metadata comparison.
       */
      function isRestoreFromMetadataCompare() {
        return restoreType === BulkRestoreType.METADATA_COMPARE;
      }

      /**
       * Determines whether the user is configuring a same-org restore.
       *
       * @returns {boolean} true if the user is configuring a same-org restore.
       */
      function isSameOrgRestore() {
        return destType === BULK_RESTORE_DEST_TYPES.SAME_ORG;
      }

      /**
       * Determines whether the "select all" checkbox is checked.
       *
       * @returns {boolean} true if the "select all" checkbox is checked.
       */
      function isSelectingAll() {
        return selectingAll;
      }

      /**
       * Determines whether we're only restoring specific object fields.
       *
       * @returns {boolean} true if we're only restoring specific object fields.
       */
      function isSpecifyingFields() {
        return specifyingFields;
      }

      /**
       * Determines whether the configured overwrite values are valid.
       *
       * @returns {boolean} true if the overwrite values are valid.
       */
      function overwriteValuesAreValid() {
        if (!isOverwritingValues()) {
          return true;
        }

        if (getOverwriteValueEntryCount() === 0) {
          return false;
        }

        // See if there are any invalid entries
        return !_.find(overwriteValueEntries, function (entry) {
          return entry.objectType === null || entry.objectField === null;
        });
      }

      /**
       * Removes an overwrite value entry by object type and field name.
       *
       * @param {string} type - the object type of the overwrite value entry to be removed.
       * @param {string} field - the field name of the overwrite value entry to be removed.
       */
      function removeOverwriteValueEntry(type, field) {
        overwriteValueEntries = _.reject(overwriteValueEntries, function (entry) {
          return entry.objectType === type && entry.objectField === field;
        });
      }

      /**
       * Removes a specific field entry by object type.
       *
       * @param {string} type - the object type of the specific field entry to be removed.
       */
      function removeSpecificFieldEntry(type) {
        specificFieldEntries = _.reject(specificFieldEntries, function (entry) {
          return entry.objectType === type;
        });
      }

      /**
       * Determines whether this restore requires the backup batch data.
       *
       * @returns {boolean} true if this restore requires the backup batch data.
       */
      function requiresBackupBatch() {
        return !isManualRestore() && usesBackupBatch();
      }

      /**
       * Determines whether this restore requires the compare batch data.
       *
       * @returns {boolean} true if this restore requires the compare batch data.
       */
      function requiresCompareBatch() {
        return !isManualRestore() && usesCompareBatch();
      }

      /**
       * Determines whether this restore requires the search object states.
       *
       * @returns {boolean} true if this restore requires the search object states.
       */
      function requiresSearchObjectStates() {
        return !isManualRestore() && usesSearchObjectStates();
      }

      /**
       * Determines whether this restore requires the search object type.
       *
       * @returns {boolean} true if this restore requires the search object type.
       */
      function requiresSearchObjectType() {
        return !isManualRestore() && usesSearchObjectType();
      }

      /**
       * Determines whether this restore requires the total object record count.
       *
       * @returns {boolean} true if this restore requires the total object record count.
       */
      function requiresTotalObjectRecordCount() {
        return !isManualRestore() && usesTotalObjectRecordCount();
      }

      /**
       * Sets the date of the selected backup for MANUAL OBJECT UPLOAD restores, MANUAL METADATA restores, and BACKUP
       * restores. Sets the date of the "from" backup for METADATA_COMPARE restores.
       *
       * @param {string} date - an ISO 8601 string representing the backup batch date.
       */
      function setBackupBatchDate(date) {
        if (UtilService.isSet(date)) {
          if (!usesBackupBatch()) {
            throw new Error(getDescription() + ' does not support backupBatchDate');
          }
          ValidationUtils.assertIsString(date, 'backupBatchDate');
        } else if (requiresBackupBatch()) {
          throw new Error(getDescription() + ' requires backupBatchDate');
        }

        backupBatchDate = date;
      }

      /**
       * Sets the id of the selected backup for MANUAL OBJECT UPLOAD restores, MANUAL METADATA restores, and BACKUP
       * restores. Sets the id of the "from" backup for METADATA_COMPARE restores.
       *
       * @param {number} id - the backup batch id.
       */
      function setBackupBatchId(id) {
        if (UtilService.isSet(id)) {
          if (!usesBackupBatch()) {
            throw new Error(getDescription() + ' does not support backupBatchId');
          }
          ValidationUtils.assertIsFinite(id, 'backupBatchId');
        } else if (requiresBackupBatch()) {
          throw new Error(getDescription() + ' requires backupBatchId');
        }

        backupBatchId = id;
      }

      /**
       * Resets backupBatchId
       */
      function resetBackupBatchId() {
        backupBatchId = undefined;
      }

      /**
       * Set the ascending
       */
      function setAscending(value) {
        ascending = value;
      }

      /**
       * Set the sort by field name
       */
      function setSortBy(value) {
        sortBy = value;
      }

      /**
       * Sets the date of the "compare against" backup for METADATA_COMPARE restores.
       *
       * @param {string} date - an ISO 8601 string representing the compare batch date.
       */
      function setCompareBatchDate(date) {
        if (UtilService.isSet(date)) {
          if (!usesCompareBatch()) {
            throw new Error(getDescription() + ' does not support compareBatchDate');
          }
          ValidationUtils.assertIsString(date, 'compareBatchDate');
        } else if (requiresCompareBatch()) {
          throw new Error(getDescription() + ' requires compareBatchDate');
        }

        compareBatchDate = date;
      }

      /**
       * Sets the id of the selected compare for MANUAL OBJECT UPLOAD restores, MANUAL METADATA restores, and COMPARE
       * restores. Sets the id of the "from" compare for METADATA_COMPARE restores.
       *
       * @param {number} id - the compare batch id.
       */
      function setCompareBatchId(id) {
        if (UtilService.isSet(id)) {
          if (!usesCompareBatch()) {
            throw new Error(getDescription() + ' does not support compareBatchId');
          }
          ValidationUtils.assertIsFinite(id, 'compareBatchId');
        } else if (requiresCompareBatch()) {
          throw new Error(getDescription() + ' requires compareBatchId');
        }

        compareBatchId = id;
      }

      /**
       * Sets the batch in which the CSV was verified for VERIFIED_CSV restores.
       *
       * @param {number} id - the CSV verification batch id.
       */
      function setCsvBatchId(id) {
        if (UtilService.isSet(id)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvBatchId');
          }
          ValidationUtils.assertIsFinite(id, 'csvBatchId');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvBatchId');
        }

        csvBatchId = id;
      }

      /**
       * Sets the completed bulk restore batch for a CSV verification.
       *
       * @param {object} batch - the completed bulk restore batch.
       */
      function setCsvCompletedBulkRestoreBatch(batch) {
        if (UtilService.isSet(batch)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvCompletedBulkRestoreBatch');
          }
          ValidationUtils.assertIsObject(batch, 'csvCompletedBulkRestoreBatch');
        }

        csvCompletedBulkRestoreBatch = batch;
      }

      /**
       * Sets the number of duplicate records found for VERIFIED_CSV restores.
       *
       * @param {number} count - the number of duplicate records.
       */
      function setCsvDuplicateCount(count) {
        if (UtilService.isSet(count)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvDuplicateCount');
          }
          ValidationUtils.assertIsFinite(count, 'csvDuplicateCount');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvDuplicateCount');
        }

        csvDuplicateCount = count;
      }

      /**
       * Sets whether the CSV verification failed.
       *
       * @param {boolean} value - true if the verification failed.
       */
      function setCsvFailed(value) {
        if (UtilService.isSet(value)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvFailed');
          }
          ValidationUtils.assertIsBoolean(value, 'csvFailed');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvFailed');
        }

        csvFailed = value;
      }

      /**
       * Sets the failure message for VERIFIED_CSV restores if the verification failed.
       *
       * @param {string} msg - the failure message.
       */
      function setCsvFailureMsg(msg) {
        if (UtilService.isSet(msg)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvFailureMsg');
          }
          ValidationUtils.assertIsString(msg, 'csvFailureMsg');
        } else if (isRestoreFromCsv() && isCsvFailed()) {
          throw new Error(getDescription() + ' requires csvFailureMsg');
        }

        csvFailureMsg = msg;
      }

      /**
       * Sets the name of the uploaded CSV file for VERIFIED_CSV restores.
       *
       * @param {string} name - the file name.
       */
      function setCsvFileName(name) {
        if (UtilService.isSet(name)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvFileName');
          }
          ValidationUtils.assertIsString(name, 'csvFileName');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvFileName');
        }

        csvFileName = name;
      }

      /**
       * Sets the number of invalid records found for VERIFIED_CSV restores.
       *
       * @param {number} count - the number of invalid records.
       */
      function setCsvInvalidCount(count) {
        if (UtilService.isSet(count)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvInvalidCount');
          }
          ValidationUtils.assertIsFinite(count, 'csvInvalidCount');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvInvalidCount');
        }

        csvInvalidCount = count;
      }

      /**
       * Sets the Salesforce object types for VERIFIED_CSV restores.
       *
       * @param {string[]} types - the list of object types.
       */
      function setCsvObjectTypes(types) {
        if (UtilService.isSet(types)) {
          if (!isRestoreFromCsv()) {
            throw new Error(getDescription() + ' does not support csvObjectTypes');
          }
          ValidationUtils.assertIsArray(types, 'csvObjectTypes');
        } else if (isRestoreFromCsv()) {
          throw new Error(getDescription() + ' requires csvObjectTypes');
        }

        csvObjectTypes = types;
      }

      /**
       * Sets the info for the org that is the destination of the restore data.
       *
       * @param {{id: number, name: string, sfId: string, accessTokenOwnerId: string}} info - the info for the
       *   destination org.
       */
      function setDestOrgInfo(info) {
        if (UtilService.isSet(info)) {
          if (isRestoreFromBackup()) {
            throw new Error(getDescription() + ' does not support destOrgInfo');
          }

          ValidationUtils.assertIsObject(info, 'destOrgInfo');

          if (UtilService.isSet(info.id)) {
            ValidationUtils.assertIsFinite(info.id, 'destOrgInfo.id');
          }

          if (UtilService.isSet(info.name)) {
            ValidationUtils.assertIsString(info.name, 'destOrgInfo.name');
          }

          if (UtilService.isSet(info.sfId)) {
            ValidationUtils.assertIsString(info.sfId, 'destOrgInfo.sfId');
          }

          if (UtilService.isSet(info.accessTokenOwnerId)) {
            ValidationUtils.assertIsFinite(info.accessTokenOwnerId, 'destOrgInfo.accessTokenOwnerId');
          }
        } else if (isCrossOrgRestore() && !isManualRestore()) {
          throw new Error(getDescription() + ' requires destOrgInfo');
        }

        destOrgInfo = info;
      }

      /**
       * Sets the destination type.
       *
       * @param {number} type - the destination type.
       */
      function setDestType(type) {
        if (UtilService.isSet(type)) {
          ValidationUtils.assertIsEnumValue(type, 'destType', BULK_RESTORE_DEST_TYPES);

          // Make sure the destination type is compatible with the restore type
          if (isRestoreFromBackup() && type !== BULK_RESTORE_DEST_TYPES.SAME_ORG) {
            throw new Error('Restore type ' + restoreType + ' requires destType ' + BULK_RESTORE_DEST_TYPES.SAME_ORG);
          }
        } else {
          // type not supplied
          switch (restoreType) {
            case BulkRestoreType.VERIFIED_CSV:
            case BulkRestoreType.METADATA_COMPARE:
              throw new Error('Restore type ' + restoreType + ' requires destType');

            // These default to SAME_ORG
            case BulkRestoreType.BACKUP:
            case BulkRestoreType.MANUAL:
            default:
              type = BULK_RESTORE_DEST_TYPES.SAME_ORG;
              break;
          }
        }

        destType = type;
      }

      /**
       * Sets whether we're ignore warnings during metadata restore.
       *
       * @param {boolean} value - true if we're ignore warnings during metadata restore.
       */
      function setIgnoreWarnings(value) {
        ValidationUtils.assertIsBoolean(value, 'ignoreWarnings');
        ignoreWarnings = value;
      }

      /**
       * Sets whether we're including children of the records being restored. For same-org restores we only
       * include children of deleted records. For cross-org restores we include children of all records.
       *
       * @param {boolean} value - true if we're including children.
       */
      function setIncludingChildren(value) {
        ValidationUtils.assertIsBoolean(value, 'includingChildren');
        includingChildren = value;
      }

      /**
       * Set last search parameters object
       * @param params - object to set
       */
      function setLastSearchParams(params) {
        lastSearchParams = params;
      }

      /**
       * Sets whether we're repairing lookup relationships.
       *
       * @param {boolean} value - true if we're repairing lookup relationships.
       */
      function setRepairingLookupRelationships(value) {
        ValidationUtils.assertIsBoolean(value, 'repairingLookupRelationships');
        repairingLookupRelationships = value;
      }

      /**
       * Sets whether we're restoring lookup relationships.
       *
       * @param {boolean} value - true if we're restoring lookup relationships.
       */
      function setRestoringLookupRelationships(value) {
        ValidationUtils.assertIsBoolean(value, 'restoringLookupRelationships');
        restoringLookupRelationships = value;
      }

      /**
       * Sets whether we're disabling salesforce automation.
       *
       * @param {boolean} value - true if we're disabling salesforce automation.
       */
      function setDisableSalesforceAutomation(value) {
        ValidationUtils.assertIsBoolean(value, 'disableSalesforceAutomation');
        disableSalesforceAutomation = value;
      }

      /**
       * Get Object Restore Type.
       */
      function getObjectRestoreType() {
        return objRestoreType;
      }

      /**
       * Sets Object Restore Type.
       *
       * @param {number} value - an BULK_RESTORE_CHILD_STRATEGIES value.
       */
      function setObjectRestoreType(value) {
        ValidationUtils.assertIsEnumValue(value, 'BULK_RESTORE_CHILD_STRATEGIES', BULK_RESTORE_CHILD_STRATEGIES);
        objRestoreType = value;
      }

      /**
       * Sets whether we're including lookup of the records being restored.
       *
       * @param {boolean} value - true if we're including children.
       */
      function setIncludingLookup(value) {
        ValidationUtils.assertIsBoolean(value, 'includingLookupRelationships');
        includingLookupRelationships = value;
      }

      /**
       * Determines whether we're including lookup of the records being restored.
       *
       * @returns {boolean} true if we're including lookup relationships.
       */
      function isIncludingLookup() {
        return includingLookupRelationships;
      }

      /**
       * Sets the way the list of object records to be restored is chosen. This only applies to MANUAL restores.
       *
       * @param {string} type - a BulkRestoreObjectListType value
       */
      function setManualObjectListType(type) {
        if (UtilService.isSet(type)) {
          if (!isManualRestore()) {
            throw new Error(getDescription() + ' does not support manualObjectListType');
          }

          ValidationUtils.assertIsEnumValue(type, 'manualObjectListType', BulkRestoreObjectListType);
        }

        manualObjectListType = type;
      }

      /**
       * Sets the metadata records.
       *
       * @param {object[]} records - the metadata records.
       */
      function setMetadataRecords(records) {
        if (UtilService.isSet(records)) {
          if (!isMetadataRestore()) {
            throw new Error(getDescription() + ' does not support metadataRecords');
          }

          ValidationUtils.assertIsArray(records, 'metadataRecords');
        } else if (isMetadataRestore()) {
          throw new Error(getDescription() + ' requires metadataRecords');
        }

        metadataRecords = records;
      }

      /**
       * Sets the current page of object records.
       *
       * @param {object[]} records - the current page of object records.
       * @param { boolean } append - should the new records be appended to the all records.
       */
      function setPageObjectRecords(records, append = false) {
        if (UtilService.isSet(records)) {
          if (!isObjectRestore()) {
            throw new Error(getDescription() + ' does not support pageObjectRecords');
          }

          ValidationUtils.assertIsArray(records, 'pageObjectRecords');
        } else if (isObjectRestore()) {
          throw new Error(getDescription() + ' requires pageObjectRecords');
        }

        checkAndMarkRecordsAreIncludingChildren(records);

        if (append) {
          pageObjectRecords.push(...records);
        } else {
          pageObjectRecords = records;
        }
      }

      /**
       * Sets whether we're overwriting any values.
       *
       * @param {boolean} value - true if we're overwriting any values.
       */
      function setOverwritingValues(value) {
        ValidationUtils.assertIsBoolean(value, 'overwritingValues');
        overwritingValues = value;
      }

      /**
       * Sets the record type.
       *
       * @param {number} type - the record type.
       */
      function setRecordType(type) {
        if (UtilService.isSet(type)) {
          ValidationUtils.assertIsEnumValue(type, 'recordType', BULK_RESTORE_TYPES);
        }

        // Make sure the record type is compatible with the restore type
        const requiredType = getRequiredRecordType();
        if (requiredType && type !== requiredType) {
          throw new Error(getDescription() + ' requires recordType ' + requiredType);
        }

        recordType = type;
      }

      /**
       * Sets the type of restore being performed.
       *
       * @param {string} type - the restore type.
       */
      function setRestoreType(type) {
        if (UtilService.isSet(type)) {
          ValidationUtils.assertIsEnumValue(type, 'restoreType', BulkRestoreType);
        }

        restoreType = type;

        // Some restore types have a known record type
        recordType = getRequiredRecordType();
      }

      /**
       * Sets the "before" backup for MANUAL OBJECT SEARCH restores.
       *
       * @param {number} id - the "before" backup batch id.
       */
      function setSearchEndBatchId(id) {
        if (UtilService.isSet(id)) {
          if (!isManualObjectSearch() && !isManualObjectBrowse()) {
            throw new Error(getDescription() + ' does not support searchEndBatchId');
          }
          ValidationUtils.assertIsFinite(id, 'searchEndBatchId');
        } else if (isManualObjectSearch()) {
          throw new Error(getDescription() + ' requires searchEndBatchId');
        }

        searchEndBatchId = id;
      }

      /**
       * Sets the updated/deleted selection for MANUAL OBJECT SEARCH restores.
       *
       * @param {number} states - the array of SOBJECT_STATE.
       */
      function setSearchObjectStates(states) {
        if (UtilService.isSet(states)) {
          if (!usesSearchObjectStates()) {
            throw new Error(getDescription() + ' does not support searchObjectStates');
          }
          ValidationUtils.assertIsArray(states, 'searchObjectStates');
        } else if (requiresSearchObjectStates()) {
          throw new Error(getDescription() + ' requires searchObjectStates');
        }

        searchObjectStates = states;
      }

      /**
       * Sets the selected Salesforce object type for MANUAL OBJECT SEARCH restores.
       *
       * @param {string} type - the object type.
       */
      function setSearchObjectType(type) {
        if (UtilService.isSet(type)) {
          if (!usesSearchObjectType()) {
            throw new Error(getDescription() + ' does not support searchObjectType');
          }
          ValidationUtils.assertIsString(type, 'searchObjectType');
        } else if (requiresSearchObjectType()) {
          throw new Error(getDescription() + ' requires searchObjectType');
        }

        searchObjectType = type;
      }

      /**
       * Sets the search page token.
       *
       * @param {string} token - the page token.
       */
      function setSearchPageToken(token) {
        searchPageToken = token;
      }

      /**
       * Sets the search page batch id.
       *
       * @param {string} pageBatchId - the page batch id.
       */
      function setSearchPageBatchId(pageBatchId) {
        searchPageBatchId = pageBatchId;
      }

      /**
       * Sets the search token.
       *
       * @param {string} token - the search token.
       */
      function setSearchToken(token) {
        searchToken = token;
      }

      /**
       * Sets the "from" backup for MANUAL OBJECT SEARCH restores.
       *
       * @param {number} id - the "from" backup batch id.
       */
      function setSearchStartBatchId(id) {
        if (UtilService.isSet(id)) {
          if (!isManualObjectSearch() && !isManualObjectBrowse()) {
            throw new Error(getDescription() + ' does not support searchStartBatchId');
          }
          ValidationUtils.assertIsFinite(id, 'searchStartBatchId');
        } else if (isManualObjectSearch()) {
          throw new Error(getDescription() + ' requires searchStartBatchId');
        }

        searchStartBatchId = id;
      }

      /**
       * Sets the "search object name" string for MANUAL OBJECT SEARCH restores.
       *
       * @param {string} text - the search text.
       */
      function setSearchText(text) {
        if (UtilService.isSet(text)) {
          if (!isManualObjectSearch() && !isManualObjectBrowse()) {
            throw new Error(getDescription() + ' does not support searchText');
          }
          ValidationUtils.assertIsString(text, 'searchText');
        }

        searchText = text;
      }

      /**
       * Set start systemModstamp
       * @returns {null}
       */
      function setStartSystemModstamp(date) {
        if (date) {
          startSystemModstamp = date.valueOf();
        } else {
          startSystemModstamp = null;
        }
      }

      /**
       * Set end systemModstamp
       * @returns {null}
       */
      function setEndSystemModstamp(date) {
        if (date) {
          endSystemModstamp = date.valueOf();
        } else {
          endSystemModstamp = null;
        }
      }

      /**
       * Sets the selected object types for restores or exports.
       *
       * @param {object} selectedObjectTypes - the selection of object types.
       */
      function setSelectedObjectTypes(selectedObjectTypesObj) {
        if (UtilService.isSet(selectedObjectTypes)) {
          if (!isManualObjectSearch() && !isManualObjectBrowse()) {
            throw new Error(getDescription() + ' does not support searchText');
          }
          ValidationUtils.assertIsObject(selectedObjectTypes, 'selectedObjectTypes');
        }

        selectedObjectTypes = selectedObjectTypesObj;
      }

      /**
       * Get selected object type object
       * @returns {Object}
       */
      function getSelectedObjectTypes() {
        return selectedObjectTypes;
      }

      /**
       * Check if mentioned count of object types were selected
       * @param {number} count
       * @returns {boolean}
       */
      function checkObjectTypeSelectedCount(count) {
        return !!(
          selectedObjectTypes &&
          (selectedObjectTypes.selectedAll ||
            (selectedObjectTypes.selectedList && selectedObjectTypes.selectedList.length >= count))
        );
      }

      /**
       * Sets the number of selected metadata records.
       *
       * @param {number} count - the number of selected records.
       */
      function setSelectedMetadataRecordCount(count) {
        ValidationUtils.assertAtLeast(count, 0, 'selectedMetadataCount');
        selectedMetadataCount = count;
      }

      /**
       * Sets whether the "select all" checkbox is checked.
       *
       * @param {boolean} value - true if the "select all" checkbox is checked.
       */
      function setSelectingAll(value) {
        ValidationUtils.assertIsBoolean(value, 'selectingAll');
        selectingAll = value;
      }

      /**
       * Sets the info for the org that is the source of the restore data.
       *
       * @param {{id: number, name: string, sfId: string}} info the info for the source org.
       */
      function setSourceOrgInfo(info) {
        if (UtilService.isSet(info)) {
          ValidationUtils.assertIsObject(info, 'sourceOrgInfo');

          if (UtilService.isSet(info.id)) {
            ValidationUtils.assertIsFinite(info.id, 'sourceOrgInfo.id');
          }

          if (UtilService.isSet(info.name)) {
            ValidationUtils.assertIsString(info.name, 'sourceOrgInfo.name');
          }

          if (UtilService.isSet(info.sfId)) {
            ValidationUtils.assertIsString(info.sfId, 'sourceOrgInfo.sfId');
          }
        } else {
          throw new Error(getDescription() + ' requires sourceOrgInfo');
        }

        sourceOrgInfo = info;
      }

      /**
       * Sets whether we're only restoring specific object fields.
       *
       * @param {boolean} value - true if we're only restoring specific object fields.
       */
      function setSpecifyingFields(value) {
        ValidationUtils.assertIsBoolean(value, 'specifyingFields');
        specifyingFields = value;
      }

      /**
       * Sets the total number of object records.
       *
       * @param {number} count - the object with total number of object records, threshold and overflow.
       */
      function setTotalObjectRecordCount(countObject) {
        if (UtilService.isSet(countObject)) {
          if (!usesTotalObjectRecordCount()) {
            throw new Error(getDescription() + ' does not support totalObjectRecordCount');
          }
          ValidationUtils.assertIsObject(countObject, 'totalObjectRecordCount');
        } else if (requiresTotalObjectRecordCount()) {
          throw new Error(getDescription() + ' requires totalObjectRecordCount');
        }

        totalObjectRecordCount = countObject;
      }

      /**
       * Sets the selected CSV file for MANUAL OBJECT UPLOAD restores.
       *
       * @param {object} file - the CSV file.
       */
      function setUploadFile(file) {
        if (UtilService.isSet(file)) {
          if (!isManualObjectUpload()) {
            throw new Error(getDescription() + ' does not support uploadFile');
          }

          ValidationUtils.assertIsObject(file, 'uploadFile');
          ValidationUtils.assertIsString(file.name, 'uploadFile.name');
        } else if (isManualObjectUpload()) {
          throw new Error(getDescription() + ' requires uploadFile');
        }

        uploadFile = file;
      }

      /**
       * Determines whether the configured specific fields are valid.
       *
       * @returns {boolean} true if the specific fields are valid.
       */
      function specificFieldsAreValid() {
        if (!isSpecifyingFields()) {
          return true;
        }

        if (getSpecificFieldEntryCount() === 0) {
          return false;
        }

        // See if there are any invalid entries
        return !_.find(specificFieldEntries, function (entry) {
          return entry.objectType === null || _.size(entry.objectFields) === 0;
        });
      }

      /**
       * Changes the selection state of an object record.
       *
       * @param {object} record - The record whose selection state is to be toggled.
       * @param {boolean} ignoreSelectingAllFlag - toggle object record selection ignoring selectingAllFlag
       */
      function toggleObjectRecordSelection(record, ignoreSelectingAllFlag = false) {
        if (selectingAll && !ignoreSelectingAllFlag) {
          throw new Error('Cannot toggle object record selection when selecting all');
        }

        if (isObjectRecordSelected(record, ignoreSelectingAllFlag)) {
          const id = getRecordId(record);

          // Remove this record from the selected list
          selectedObjectRecords = _.reject(selectedObjectRecords, function (selectedRecord) {
            return getRecordId(selectedRecord) === id;
          });
          clearSelectionsForRecord(record);
        } else {
          selectedObjectRecords.push(record);
        }
      }

      /**
       * Determines whether this restore uses the backup batch data.
       *
       * @returns {boolean} true if this restore uses the backup batch data.
       */
      function usesBackupBatch() {
        return !isManualObjectSearch();
      }

      /**
       * Determines whether this restore uses the compare batch data.
       *
       * @returns {boolean} true if this restore uses the compare batch data.
       */
      function usesCompareBatch() {
        return isRestoreFromMetadataCompare();
      }

      /**
       * Determines whether this restore uses the search object state.
       *
       * @returns {boolean} true if this restore uses the search object state.
       */
      function usesSearchObjectStates() {
        return isManualObjectSearch() || isRestoreFromBackup() || isRestoreFromCsv() || isManualObjectBrowse();
      }

      /**
       * Determines whether this restore uses the search object type.
       *
       * @returns {boolean} true if this restore uses the search object type.
       */
      function usesSearchObjectType() {
        return isManualObjectSearch() || isManualObjectBrowse() || isRestoreFromBackup();
      }

      /**
       * Determines whether this restore uses the total object record count.
       *
       * @returns {boolean} true if this restore uses the total object record count.
       */
      function usesTotalObjectRecordCount() {
        return isManualObjectSearch() || isManualObjectBrowse() || isRestoreFromBackup() || isRestoreFromCsv();
      }

      /**
       * Reset all includeChildren flag for child records.
       * @param {array} children
       */
      function resetIncludeChildrenFlagForChildren(children) {
        if (angular.isArray(children) && children.length) {
          children.forEach((child) => {
            child.includeChildren = false;
            const children = tryToGetChildrenOfRecord(child);
            resetIncludeChildrenFlagForChildren(children);
          });
        }
      }

      /**
       * Reset all selected flag for child records.
       * @param {array} children
       */
      function uncheckSelectedFlagForChildren(children) {
        if (angular.isArray(children) && children.length) {
          children.forEach((child) => {
            child.isSelected = false;
            uncheckSelectedFlagForChildren(child.children);
          });
        }
      }

      /**
       * Reset all restore preferences including all descendants for specific record
       * @param record
       */
      function clearSelectionsForRecord(record) {
        clearRecordIncludeChildrenWithDescendants(record);
        const children = childRecords[record.recordId];
        uncheckSelectedFlagForChildren(children);
        record.isSelected = false;
        removeRecordFromIncludeChildrenList(record);
      }

      /**
       * Reset all restore preferences including all descendants for all selected records
       */
      function clearAllSelections() {
        setSelectingAll(false);

        const selectedRecords = getSelectedObjectRecords();
        selectedRecords.forEach((record) => {
          clearSelectionsForRecord(record);
        });

        clearSelectedObjectRecords();
        clearRecordsToIncludeChildren();
      }

      /**
       * Store record, marked to include children
       * @param record
       */
      function pushRecordToIncludeChildren(record) {
        recordsToIncludeChildren.push(record);
      }

      /**
       * Remove record from the includeChildren list
       * @param record
       */
      function removeRecordFromIncludeChildrenList(record) {
        const id = getRecordId(record);

        // Remove this record from the selected list
        recordsToIncludeChildren = _.reject(recordsToIncludeChildren, function (savedRecord) {
          return getRecordId(savedRecord) === id;
        });
      }

      /**
       * Reset the list of records to be restored including children
       */
      function clearRecordsToIncludeChildren() {
        for (const record of recordsToIncludeChildren) {
          record.includeChildren = false;
        }
        recordsToIncludeChildren = [];
      }

      /**
       * Reset the list of records to be restored including children and their children recursively
       */
      function clearAllIncludeChildrenWithDescendants() {
        for (const record of recordsToIncludeChildren) {
          clearRecordIncludeChildrenWithDescendants(record);
        }
        recordsToIncludeChildren = [];
      }

      /**
       * Reset record to be restored including children and its children recursively
       * @param {object} record
       */
      function clearRecordIncludeChildrenWithDescendants(record) {
        record.includeChildren = false;
        const children = childRecords[record.recordId];
        resetIncludeChildrenFlagForChildren(children);
      }

      /**
       * Check if received records are including children to restore and mark them
       * @param records
       */
      function checkAndMarkRecordsAreIncludingChildren(records) {
        records.forEach((record) => {
          let id;

          try {
            id = getRecordId(record);
          } catch (err) {
            console.log(err);

            return;
          }

          const foundRecord = _.find(recordsToIncludeChildren, (savedRecord) => {
            const savedRecordId = getRecordId(savedRecord);

            return savedRecordId === id;
          });

          if (foundRecord) {
            foundRecord.includeChildren = true;
            record.includeChildren = true;
          }
        });

        return records;
      }

      /**
       * Check if record has partial selection of children recursively. Note that if all children are seleted or
       * no one child is not selected then method returns false.
       * @param record
       */
      function hasPartialSelectionOfChildren(record) {
        const childrenSelection = calculateSelectedAndUnselectedRecordChildren(record);
        return childrenSelection.selectedCount > 0 && childrenSelection.unselectedCount > 0;
      }

      /**
       * Calculate the sum of record's selected and unselected children recursively
       * @param record
       * @returns {object} result object
       */
      function calculateSelectedAndUnselectedRecordChildren(record) {
        const result = {
          selectedCount: 0,
          unselectedCount: 0
        };
        const recordChildren = tryToGetChildrenOfRecord(record);
        if (!recordChildren || recordChildren.length === 0) {
          return result;
        }
        for (const child of recordChildren) {
          if (child.isSelected) {
            result.selectedCount++;
          } else {
            result.unselectedCount++;
          }
          const childChildren = tryToGetChildrenOfRecord(record);
          if (childChildren && childChildren.length > 0) {
            const childrenResult = calculateSelectedAndUnselectedRecordChildren(child);
            result.selectedCount += childrenResult.selectedCount;
            result.unselectedCount += childrenResult.unselectedCount;
          }
        }
        return result;
      }

      /**
       * On different levels of tree children are kept differently. On top the children are in separate array but
       * on different levels they are in children field.
       * @param record
       * @returns {object} children
       */
      function tryToGetChildrenOfRecord(record) {
        //get children records on top of the tree
        let recordChildren = childRecords[record.recordId];
        if (!recordChildren) {
          //get children records on lower levels
          return record.children;
        }
        return recordChildren;
      }
    }
  }
})();
